import { apiEndpoints } from "@/src/config/api/endpoints";

import { api } from "./base_service";
import type { ApiError, Paginated, Result } from "./types";

/** A `/company/webhooks/` entry as the backend sends it (snake_case). */
export interface WebhookRaw {
  id: string;
  url: string;
  /** Human label for the endpoint — added to the response 2026-06-18 (Alejandro). */
  name?: string;
  is_sandbox: boolean;
  failures?: number;
  disabled?: boolean;
  /** Signing secret — returned only on creation (contract pending confirm). */
  hash_secret?: string;
}

/** A webhook at the camelCase boundary. */
export interface Webhook {
  id: string;
  url: string;
  /** Human label for the endpoint (backend-provided). May be empty. */
  name: string;
  /** Sandbox (test) vs production endpoint. */
  isSandbox: boolean;
  /** Consecutive delivery failures the backend has recorded. */
  failures: number;
  /** Backend-disabled after repeated failures; re-enable via `reEnableWebhook`. */
  disabled: boolean;
  /** Signing secret, present ONLY on the create response — surface once, never persist. */
  hashSecret?: string;
}

export function adaptWebhook(raw: WebhookRaw): Webhook {
  return {
    id: String(raw.id),
    url: raw.url ?? "",
    name: raw.name ?? "",
    isSandbox: !!raw.is_sandbox,
    failures: raw.failures ?? 0,
    disabled: !!raw.disabled,
    // Only set when the backend returns it (create); `undefined` otherwise — note
    // `toEqual` ignores undefined keys so existing list/adapt tests are unaffected.
    hashSecret: raw.hash_secret,
  };
}

export async function listWebhooks(): Promise<Result<Webhook[]>> {
  try {
    const data = await api.get<Paginated<WebhookRaw> | WebhookRaw[]>(
      apiEndpoints.webhooks().list(),
    );
    const list = Array.isArray(data) ? data : (data.results ?? []);
    return { ok: true, value: list.map(adaptWebhook) };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

export async function createWebhook(url: string, isSandbox: boolean): Promise<Result<Webhook>> {
  try {
    const data = await api.post<WebhookRaw>(apiEndpoints.webhooks().create(), {
      url,
      is_sandbox: isSandbox,
    });
    return { ok: true, value: adaptWebhook(data) };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

export async function deleteWebhook(id: string): Promise<Result<void>> {
  try {
    await api.delete<void>(apiEndpoints.webhooks().delete(id));
    return { ok: true, value: undefined };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

export async function reEnableWebhook(id: string): Promise<Result<Webhook>> {
  try {
    const data = await api.patch<WebhookRaw>(apiEndpoints.webhooks().update(id), {});
    return { ok: true, value: adaptWebhook(data) };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

export async function updateWebhook(
  id: string,
  url: string,
  isSandbox: boolean,
): Promise<Result<Webhook>> {
  try {
    const data = await api.patch<WebhookRaw>(apiEndpoints.webhooks().update(id), {
      url,
      is_sandbox: isSandbox,
    });
    return { ok: true, value: adaptWebhook(data) };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

export async function pingWebhook(id: string): Promise<Result<{ delivered: boolean }>> {
  try {
    const data = await api.post<{ delivered?: boolean }>(apiEndpoints.webhooks().ping(id), {});
    return { ok: true, value: { delivered: data?.delivered ?? true } };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}
